// plan-20 S01/S03/S04: the vague-ifier stage widget — bible §38 (stage
// contract), §42 (grid area: stage body; z-layers: content at stage-body plane,
// tuning sheet as a body-relative absolute overlay following the
// vt-transparency-sheet precedent; tap tiers via --vt-tap/--vt-click).
//
// The command IS the user gesture: the camera is requested ONLY on the
// `vague mirror` path, never on mount or any other path (the doctrine analog of
// PART IX #10 no-autoplay; see the mirror invariant proposed in the bible).

import type { StageWidget, StageHandle, StageCtx } from '../stage/types'
import { load, set } from '../../core/save'
import { play } from '../../audio/sfx'
import { VagueEngine, PASSES } from './engine'
import type { CaptionPos } from './preset'

function bumpVague(field: 'blessings' | 'loopsWoven' | 'scries'): void {
  const s = load()
  const v = s.vagueifier ?? { blessings: 0, loopsWoven: 0, scries: 0 }
  set('vagueifier', { ...v, [field]: v[field] + 1 })
}

function makeButton(id: string, label: string): HTMLButtonElement {
  const b = document.createElement('button')
  b.type = 'button'
  b.className = `vt-vagueifier-btn ${id}`
  b.textContent = label
  return b
}

export const vagueifierWidget: StageWidget = {
  id: 'vagueifier',
  label: 'CORONATION // VAGUE-IFIER',

  mount(host: HTMLElement, ctx: StageCtx): StageHandle {
    const root = document.createElement('div')
    root.className = 'vt-widget-body vt-vagueifier'

    // ── intake state (no image) ─────────────────────────────────────────
    const dropzone = document.createElement('div')
    dropzone.className = 'vt-vagueifier-dropzone'
    const dzMain = document.createElement('div')
    dzMain.className = 'vt-vagueifier-dz-main'
    dzMain.textContent = 'drop an image. click to choose. paste (ctrl+v).'
    const dzHint = document.createElement('div')
    dzHint.className = 'vt-vagueifier-dz-hint'
    dzHint.textContent = 'nothing leaves this page. all processing is local.'
    const dzBtns = document.createElement('div')
    dzBtns.className = 'vt-vagueifier-dz-btns'
    const specimenBtn = makeButton('specimen', '[ summon test specimen ]')
    const mirrorBtn = makeButton('mirror', '[ open the mirror ]')
    dzBtns.append(specimenBtn, mirrorBtn)
    const dzHint2 = document.createElement('div')
    dzHint2.className = 'vt-vagueifier-dz-hint'
    dzHint2.textContent = "john dee's was obsidian."
    const dzStatus = document.createElement('div')
    dzStatus.className = 'vt-vagueifier-dz-status'
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = 'image/*'
    fileInput.hidden = true
    dropzone.append(dzMain, dzHint, dzBtns, dzHint2, dzStatus, fileInput)

    // ── workspace (image loaded) ────────────────────────────────────────
    const workspace = document.createElement('div')
    workspace.className = 'vt-vagueifier-workspace'
    workspace.style.display = 'none'

    const stageBox = document.createElement('div')
    stageBox.className = 'vt-vagueifier-stagebox'
    const canvas = document.createElement('canvas')
    canvas.className = 'vt-vagueifier-canvas'
    stageBox.appendChild(canvas)
    workspace.appendChild(stageBox)

    // slider row
    const sliderRow = document.createElement('div')
    sliderRow.className = 'vt-vagueifier-row vt-vagueifier-slider-row'
    const sliderLabel = document.createElement('span')
    sliderLabel.className = 'vt-vagueifier-slider-label'
    sliderLabel.textContent = 'vagueness'
    const slider = document.createElement('input')
    slider.type = 'range'
    slider.min = '0'
    slider.max = '48'
    slider.step = '1'
    slider.value = '0'
    slider.className = 'vt-vagueifier-slider'
    slider.setAttribute('aria-label', 'vagueness')
    const readout = document.createElement('span')
    readout.className = 'vt-vagueifier-read'
    readout.textContent = '0/48'
    sliderRow.append(sliderLabel, slider, readout)

    const stageLine = document.createElement('div')
    stageLine.className = 'vt-vagueifier-stageline'
    stageLine.textContent = 'pristine. for now.'

    // button row
    const btnRow = document.createElement('div')
    btnRow.className = 'vt-vagueifier-row vt-vagueifier-btn-row'
    const tuningBtn = makeButton('tuning', '[ tuning ]')
    const transmitBtn = makeButton('transmit', '[ transmit ]')
    const blessBtn = makeButton('bless', '[ bless ]')
    const weaveBtn = makeButton('weave', '[ weave loop ]')
    weaveBtn.title = 'export as looping gif'
    const newBtn = makeButton('new', '[ new image ]')
    btnRow.append(tuningBtn, transmitBtn, blessBtn, weaveBtn, newBtn)

    workspace.append(sliderRow, stageLine, btnRow)

    root.append(dropzone, workspace)

    // hidden video sink for the mirror
    const video = document.createElement('video')
    video.setAttribute('playsinline', '')
    video.muted = true
    video.hidden = true
    root.appendChild(video)

    host.appendChild(root)

    // ── tuning sheet (absolute overlay inside the stage body, §42) ───────
    // follows the vt-transparency-sheet precedent: body-relative z, well under
    // the modal-portal 80 — no new global z-index.
    let tuningSheet: HTMLElement | null = null
    let tuningOutside: ((e: MouseEvent) => void) | null = null

    function reducedMotion(): boolean {
      return document.body.classList.contains('vt-reduced-motion') || load().reducedMotion
    }

    const engine = new VagueEngine({
      canvas,
      video,
      stageBox,
      onStageLine: (text) => { stageLine.textContent = text },
      emitOracle: (line) => ctx.emitOracle(line),
      reducedMotion,
      onSourceLoaded: () => {
        dropzone.style.display = 'none'
        workspace.style.display = 'flex'
        // mirror hides the transmit button (it is already live)
        transmitBtn.style.display = engine.isMirror() ? 'none' : ''
        requestAnimationFrame(() => engine.lockDisplaySize())
      },
      onWeaveBusy: (busy) => {
        weaveBtn.textContent = busy ? '[ weaving... ]' : '[ weave loop ]'
        weaveBtn.classList.toggle('is-busy', busy)
      },
    })

    // ── intake wiring ───────────────────────────────────────────────────
    dropzone.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).tagName !== 'BUTTON') fileInput.click()
    })
    dropzone.addEventListener('dragover', (e) => { e.preventDefault(); dropzone.classList.add('drag') })
    dropzone.addEventListener('dragleave', () => dropzone.classList.remove('drag'))
    dropzone.addEventListener('drop', (e) => {
      e.preventDefault()
      dropzone.classList.remove('drag')
      engine.loadFile(e.dataTransfer?.files[0])
    })
    fileInput.addEventListener('change', () => engine.loadFile(fileInput.files?.[0]))
    specimenBtn.addEventListener('click', () => engine.testSpecimen())

    async function beginMirror(): Promise<void> {
      dzStatus.textContent = ''
      const ok = await engine.openMirror()
      if (ok) {
        bumpVague('scries')
      } else {
        dzStatus.textContent = 'the mirror declined. probably.'
        play('command_reject')
      }
    }
    mirrorBtn.addEventListener('click', () => { void beginMirror() })

    // paste: image only — the terminal owns text input, we only claim images.
    const onPaste = (e: ClipboardEvent): void => {
      for (const item of e.clipboardData?.items ?? []) {
        if (item.type.startsWith('image/')) { engine.loadFile(item.getAsFile()); break }
      }
    }
    window.addEventListener('paste', onPaste)

    // ── slider wiring ───────────────────────────────────────────────────
    slider.addEventListener('input', () => {
      const nv = Number(slider.value)
      readout.textContent = `${nv}/48`
      engine.setVagueness(nv)
    })
    slider.addEventListener('change', () => engine.commitVagueness())

    // ── button wiring ───────────────────────────────────────────────────
    tuningBtn.addEventListener('click', () => toggleTuning())
    transmitBtn.addEventListener('click', () => {
      const on = engine.toggleTransmit()
      transmitBtn.textContent = on ? '[ halt transmission ]' : '[ transmit ]'
      transmitBtn.classList.toggle('is-live', on)
    })
    blessBtn.addEventListener('click', async () => {
      const ok = await engine.bless()
      if (ok) { play('quorum_chime'); bumpVague('blessings') }
      else play('command_reject')
    })
    weaveBtn.addEventListener('click', async () => {
      const res = await engine.exportLoop()
      if (res.ok) { play('quorum_chime'); bumpVague('loopsWoven') }
      else if (!res.aborted) play('command_reject')
    })
    newBtn.addEventListener('click', () => {
      engine.reset()
      transmitBtn.textContent = '[ transmit ]'
      transmitBtn.classList.remove('is-live')
      transmitBtn.style.display = ''
      workspace.style.display = 'none'
      dropzone.style.display = ''
      dzStatus.textContent = ''
      slider.value = '0'
      readout.textContent = '0/48'
      stageLine.textContent = 'pristine. for now.'
    })

    // ── peek + pixel-inspector easter egg (pointer only; no keyboard) ────
    canvas.addEventListener('pointerdown', (e) => { e.preventDefault(); engine.startPeek() })
    window.addEventListener('pointerup', onPointerUp)
    canvas.addEventListener('pointerleave', () => engine.endPeek())
    canvas.addEventListener('click', (e) => {
      const rect = canvas.getBoundingClientRect()
      const px = (e.clientX - rect.left) * (canvas.width / rect.width)
      const py = (e.clientY - rect.top) * (canvas.height / rect.height)
      engine.toggleInspectorAt(px, py)
    })
    function onPointerUp(): void { engine.endPeek() }

    // ── display-size re-lock ─────────────────────────────────────────────
    const ro = new ResizeObserver(() => engine.lockDisplaySize())
    ro.observe(stageBox)
    let unsubViewport = (): void => {}
    import('../../core/viewport').then(({ onViewportChange }) => {
      unsubViewport = onViewportChange(() => engine.lockDisplaySize())
    }).catch(() => {})

    // ── tuning sheet build/teardown ─────────────────────────────────────
    function buildTuning(): HTMLElement {
      const sheet = document.createElement('div')
      sheet.className = 'vt-vagueifier-tuning'

      const close = document.createElement('button')
      close.type = 'button'
      close.className = 'vt-vagueifier-tuning-close'
      close.textContent = '[ ✕ ]'
      close.addEventListener('click', () => closeTuning())
      sheet.appendChild(close)

      const h1 = document.createElement('h3')
      h1.className = 'vt-vagueifier-tuning-h'
      h1.textContent = '// pass envelopes — multiplier 0-2'
      sheet.appendChild(h1)

      const passList = document.createElement('div')
      passList.className = 'vt-vagueifier-passlist'
      for (const p of PASSES) {
        const row = document.createElement('div')
        row.className = 'vt-vagueifier-pass-row'
        const nameLabel = document.createElement('label')
        nameLabel.className = 'vt-vagueifier-pass-name'
        const cb = document.createElement('input')
        cb.type = 'checkbox'
        cb.checked = engine.state.passes[p.id].on
        cb.addEventListener('change', () => engine.setPass(p.id, { on: cb.checked }))
        nameLabel.appendChild(cb)
        nameLabel.appendChild(document.createTextNode(' ' + p.label))
        const mSlider = document.createElement('input')
        mSlider.type = 'range'
        mSlider.min = '0'
        mSlider.max = '2'
        mSlider.step = '0.05'
        mSlider.value = String(engine.state.passes[p.id].mult)
        mSlider.className = 'vt-vagueifier-pass-slider'
        const mv = document.createElement('span')
        mv.className = 'vt-vagueifier-pass-mv'
        mv.textContent = engine.state.passes[p.id].mult.toFixed(2)
        mSlider.addEventListener('input', () => {
          const val = parseFloat(mSlider.value)
          mv.textContent = val.toFixed(2)
          engine.setPass(p.id, { mult: val })
        })
        row.append(nameLabel, mSlider, mv)
        passList.appendChild(row)
      }
      sheet.appendChild(passList)

      // caption controls
      const capH = document.createElement('h3')
      capH.className = 'vt-vagueifier-tuning-h'
      capH.textContent = '// caption — rationed. text is doctrine.'
      sheet.appendChild(capH)
      const capRow = document.createElement('div')
      capRow.className = 'vt-vagueifier-pass-row'
      const capLabel = document.createElement('label')
      capLabel.className = 'vt-vagueifier-pass-name'
      const capCb = document.createElement('input')
      capCb.type = 'checkbox'
      capCb.checked = engine.state.caption.on
      capCb.addEventListener('change', () => engine.setCaption({ on: capCb.checked }))
      capLabel.appendChild(capCb)
      capLabel.appendChild(document.createTextNode(' probably.'))
      const capPos = document.createElement('select')
      capPos.className = 'vt-vagueifier-select'
      for (const [val, text] of [['bl', 'bottom left'], ['br', 'bottom right'], ['tl', 'top left'], ['tr', 'top right'], ['cb', 'center bottom']] as [CaptionPos, string][]) {
        const opt = document.createElement('option')
        opt.value = val
        opt.textContent = text
        if (val === engine.state.caption.pos) opt.selected = true
        capPos.appendChild(opt)
      }
      capPos.addEventListener('change', () => engine.setCaption({ pos: capPos.value as CaptionPos }))
      const capSize = document.createElement('input')
      capSize.type = 'range'
      capSize.min = '2'
      capSize.max = '12'
      capSize.step = '0.5'
      capSize.value = String(engine.state.caption.size)
      capSize.className = 'vt-vagueifier-pass-slider'
      const capSizeRead = document.createElement('span')
      capSizeRead.className = 'vt-vagueifier-pass-mv'
      capSizeRead.textContent = engine.state.caption.size.toFixed(1)
      capSize.addEventListener('input', () => {
        const val = parseFloat(capSize.value)
        capSizeRead.textContent = val.toFixed(1)
        engine.setCaption({ size: val })
      })
      capRow.append(capLabel, capPos, capSize, capSizeRead)
      sheet.appendChild(capRow)

      // inspector toggle
      const inspH = document.createElement('h3')
      inspH.className = 'vt-vagueifier-tuning-h'
      inspH.textContent = '// instruments'
      sheet.appendChild(inspH)
      const inspRow = document.createElement('div')
      inspRow.className = 'vt-vagueifier-pass-row'
      const inspLabel = document.createElement('label')
      inspLabel.className = 'vt-vagueifier-pass-name'
      const inspCb = document.createElement('input')
      inspCb.type = 'checkbox'
      inspCb.checked = engine.state.inspector
      inspCb.addEventListener('change', () => engine.setInspector(inspCb.checked))
      inspLabel.appendChild(inspCb)
      inspLabel.appendChild(document.createTextNode(' pixel inspector — or click the pixel.'))
      inspRow.append(inspLabel)
      sheet.appendChild(inspRow)

      // json copy/apply
      const jsonH = document.createElement('h3')
      jsonH.className = 'vt-vagueifier-tuning-h'
      jsonH.textContent = '// tuning json'
      sheet.appendChild(jsonH)
      const jsonRow = document.createElement('div')
      jsonRow.className = 'vt-vagueifier-pass-row'
      const copyBtn = makeButton('copy', '[ copy json ]')
      const applyBtn = makeButton('apply', '[ apply json ]')
      jsonRow.append(copyBtn, applyBtn)
      sheet.appendChild(jsonRow)
      const jsonBox = document.createElement('textarea')
      jsonBox.className = 'vt-vagueifier-jsonbox'
      jsonBox.spellcheck = false
      jsonBox.value = engine.toJson()
      sheet.appendChild(jsonBox)
      copyBtn.addEventListener('click', () => {
        jsonBox.value = engine.toJson()
        navigator.clipboard?.writeText(engine.toJson()).catch(() => {})
        play('command_accept')
      })
      applyBtn.addEventListener('click', () => {
        if (engine.applyJson(jsonBox.value)) {
          closeTuning()
          play('command_accept')
        } else {
          stageLine.textContent = '// tuning json unreadable. the pattern resists.'
          play('command_reject')
        }
      })

      return sheet
    }

    function toggleTuning(): void {
      if (tuningSheet) { closeTuning() } else { openTuning() }
    }
    function openTuning(): void {
      tuningSheet = buildTuning()
      host.appendChild(tuningSheet)
      tuningOutside = (e: MouseEvent): void => {
        const t = e.target as Node
        if (tuningSheet && !tuningSheet.contains(t) && t !== tuningBtn) closeTuning()
      }
      setTimeout(() => { if (tuningOutside) document.addEventListener('click', tuningOutside) }, 0)
    }
    function closeTuning(): void {
      if (tuningOutside) { document.removeEventListener('click', tuningOutside); tuningOutside = null }
      tuningSheet?.remove()
      tuningSheet = null
    }

    // ── activation: the command is the gesture (mirror only) ─────────────
    if (ctx.args[0] === 'mirror') {
      void beginMirror()
    }

    return {
      dismiss() {
        window.removeEventListener('paste', onPaste)
        window.removeEventListener('pointerup', onPointerUp)
        ro.disconnect()
        unsubViewport()
        closeTuning()
        engine.destroy()
        video.remove()
      },
      onSwap(args?: string[]) {
        if (args?.[0] === 'mirror' && !engine.isMirror()) void beginMirror()
      },
    }
  },
}
